<template>
  <Container>
    <PageHeader :items="items" />

    <div v-if="booking" class="row">
      <div class="col-md-6">
        <div class="card mb-4">
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-8">
                <h4 class="card-title">
                  Booking: {{ booking.attributes.customer.first_name }} {{ booking.attributes.customer.last_name }}
                </h4>
              </div>

              <div class="col-4 text-right">
                <b-button v-b-modal.reservation-modal @click="editReservation" variant="outline-light">
                  <i class="fa fa-pencil-alt"></i> Edit
                </b-button>
              </div>
            </div>

            <div class="mb-1">
              Arrival: {{ booking.attributes.reservations.data[0].attributes.checkin | naturalDay }}
            </div>
            <div class="mb-1">
              Departure: {{ booking.attributes.reservations.data[0].attributes.checkout | naturalDay }}
            </div>
            <div class="mb-1">Accommodation: {{ booking.attributes.reservations.data[0].attributes.lodging.name }}</div>
            <div class="mb-1" v-if="booking.attributes.employee">Channel: Direct</div>
            <div class="mb-1" v-else>Channel: Booking Engine</div>
            <div class="mb-1">
              Booked at: {{ booking.attributes.reservations.data[0].attributes.created_at | naturalDayTime }}
            </div>
            <div class="mb-1">Adults: {{ booking.attributes.reservations.data[0].attributes.adults }}</div>
            <div class="mb-1">Children: {{ booking.attributes.reservations.data[0].attributes.children }}</div>
            <div class="mb-1">Booking ID: {{ booking.id }}</div>

            <b-modal size="lg" id="reservation-modal" title="Edit Reservation Details" title-class="font-18">
              <b-alert show variant="warning">
                Note: Price, Additional price & Discount are not auto calculated on booking update.
              </b-alert>

              <ValidationObserver ref="reservation_form" v-slot="{ handleSubmit }">
                <b-form id="reservation-form" @submit.prevent="handleSubmit(onReservationSubmit)">
                  <ValidationProvider vid="reservations.lodging" rules="required" v-slot="{ errors }">
                    <b-form-group label="Lodging" label-for="lodging">
                      <Multiselect
                        id="lodging"
                        v-model="lodging"
                        :options="lodgingsName"
                        track-by="value"
                        label="text"
                        :allow-empty="false"
                        deselect-label="Selected"
                      ></Multiselect>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </ValidationProvider>

                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Arrival" label-for="checkin">
                        <ValidationProvider vid="reservations.checkin" rules="required" v-slot="{ errors }">
                          <DatePicker
                            v-model="checkin"
                            class="w-100"
                            id="checkin"
                            placeholder="Checkin Date"
                            format="DD-MM-YYYY"
                            :disabled-date="disabledAfterCheckout"
                          ></DatePicker>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Departure" label-for="checkout">
                        <ValidationProvider vid="reservations.checkout" rules="required" v-slot="{ errors }">
                          <DatePicker
                            v-model="checkout"
                            class="w-100"
                            id="checkout"
                            placeholder="Checkout Date"
                            format="DD-MM-YYYY"
                            :disabled-date="disabledBeforeCheckin"
                          ></DatePicker>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Adults" label-for="adults">
                        <ValidationProvider rules="required|minimum:0" v-slot="{ errors }">
                          <b-form-input
                            v-model="reservation.adults"
                            type="number"
                            id="adults"
                            placeholder="No of adults"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Children" label-for="children">
                        <ValidationProvider rules="required|minimum:0" v-slot="{ errors }">
                          <b-form-input
                            v-model="reservation.children"
                            type="number"
                            id="children"
                            placeholder="No of children"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-form-group class="ml-0" label="Notes" label-for="notes">
                    <textarea
                      id="notes"
                      v-model="booking_obj.notes"
                      class="form-control"
                      rows="5"
                      placeholder="Enter notes"
                    ></textarea>
                  </b-form-group>
                </b-form>
              </ValidationObserver>

              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('reservation-modal')">Close</b-button>
                <b-button form="reservation-form" type="submit" variant="primary">Save</b-button>
              </template>
            </b-modal>
          </div>
        </div>

        <div class="card mb-4">
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-8">
                <h4 class="card-title">Customer</h4>
              </div>

              <div class="col-4 text-right">
                <b-button v-b-modal.customer-modal @click="editCustomer" variant="outline-light">
                  <i class="fa fa-pencil-alt"></i> Edit
                </b-button>
              </div>
            </div>

            <div class="mb-1">
              Name: {{ booking.attributes.customer.first_name }} {{ booking.attributes.customer.last_name }}
            </div>
            <div class="mb-1">Email: {{ booking.attributes.customer.email }}</div>
            <div class="mb-1">Phone #: {{ booking.attributes.customer.phone_number }}</div>

            <b-modal size="lg" id="customer-modal" title="Edit Customer Details" title-class="font-18">
              <ValidationObserver ref="form" v-slot="{ handleSubmit }">
                <b-form id="customer-form" @submit.prevent="handleSubmit(onCustomerSubmit)">
                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="First name" label-for="first_name">
                        <ValidationProvider rules="required|min:2" v-slot="{ errors }">
                          <b-form-input
                            id="first_name"
                            v-model="customer.first_name"
                            placeholder="Enter first name"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Last Name" label-for="last_name">
                        <ValidationProvider rules="required|min:2" v-slot="{ errors }">
                          <b-form-input
                            id="last_name"
                            v-model="customer.last_name"
                            placeholder="Enter last Name"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-form-group label="Email" label-for="email">
                    <ValidationProvider vid="customer.email" rules="email|required" v-slot="{ errors }">
                      <b-form-input
                        id="email"
                        v-model="customer.email"
                        type="email"
                        placeholder="Enter email"
                      ></b-form-input>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>

                  <b-form-group label="Phone number" label-for="phone_number">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <b-form-input
                        id="phone_number"
                        v-model="customer.phone_number"
                        placeholder="Enter phone #"
                      ></b-form-input>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-form>
              </ValidationObserver>

              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('customer-modal')">Close</b-button>
                <b-button form="customer-form" type="submit" variant="primary">Save</b-button>
              </template>
            </b-modal>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card mb-4">
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-8">
                <h4 class="card-title">Payment Details</h4>
              </div>

              <div class="col-4 text-right">
                <b-button @click="editReservation()" v-b-modal.payment-modal variant="outline-light">
                  <i class="fa fa-pencil-alt"></i> Edit
                </b-button>
              </div>
            </div>

            <div class="mb-1">Price: € {{ booking.attributes.reservations.data[0].attributes.price }}</div>
            <div class="mb-1">Add ons: € {{ booking.attributes.reservations.data[0].attributes.additional_price }}</div>
            <div class="mb-3">Discount: € {{ booking.attributes.reservations.data[0].attributes.discount_value }}</div>
            <div>Total: € {{ totalCost }}</div>

            <b-modal size="lg" id="payment-modal" title="Edit Payment Details" title-class="font-18">
              <ValidationObserver ref="form" v-slot="{ handleSubmit }">
                <b-form id="payment-form" @submit.prevent="handleSubmit(onReservationSubmit)">
                  <b-form-group label="Price" label-for="price">
                    <ValidationProvider rules="required|greater:@discount_value" v-slot="{ errors }">
                      <b-form-input
                        id="price"
                        v-model="reservation.price"
                        type="number"
                        step=".01"
                        placeholder="Enter price"
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>

                  <b-form-group label="Additional Price" label-for="additional_price">
                    <b-form-input
                      id="additional_price"
                      v-model="reservation.additional_price"
                      type="number"
                      step=".01"
                      placeholder="Enter additional price"
                    ></b-form-input>
                  </b-form-group>

                  <ValidationProvider vid="discount_value">
                    <b-form-group label="Discount" label-for="discount_value">
                      <b-form-input
                        id="discount_value"
                        step=".01"
                        v-model="reservation.discount_value"
                        type="number"
                        placeholder="Enter discount value"
                      ></b-form-input>
                    </b-form-group>
                  </ValidationProvider>
                </b-form>
              </ValidationObserver>

              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('reservation-modal')">Close</b-button>
                <b-button form="payment-form" type="submit" variant="primary">Save</b-button>
              </template>
            </b-modal>
          </div>
        </div>

        <div class="card mb-4">
          <div class="card-body">
            <div class="row align-self-center mb-3">
              <div class="col-8">
                <h4 class="card-title">Add Ons</h4>
              </div>

              <div class="col-4 text-right">
                <b-button @click="clearBooking" v-b-modal.addon-modal variant="outline-light">
                  <i class="fa fa-plus"></i> Add
                </b-button>
              </div>
            </div>

            <div v-if="booking.attributes.reservations.data[0].attributes.reserved_add_ons.length">
              <div
                v-for="reserved_add_on in booking.attributes.reservations.data[0].attributes.reserved_add_ons"
                :key="reserved_add_on.id"
              >
                <div class="row mb-1">
                  <div class="col-8">{{ reserved_add_on.title }}: €{{ reserved_add_on.price }}</div>
                  <div class="col-4 text-right">
                    <i
                      role="button"
                      v-b-modal.delete-modal
                      id="delete_addon"
                      @click="editAddOn(reserved_add_on)"
                      class="fa fa-trash text-danger"
                    ></i>
                  </div>
                </div>
              </div>
            </div>

            <div v-else>
              No add ons selected for this booking
            </div>

            <b-modal size="lg" id="addon-modal" title="Add Add On" title-class="font-18">
              <b-alert show variant="warning">
                Note: Price, Additional price & Discount are not auto calculated on booking update.
              </b-alert>

              <ValidationObserver v-slot="{ handleSubmit }">
                <b-form id="addon-form" @submit.prevent="handleSubmit(onAddOnSubmit)">
                  <b-form-group label="Add ons" label-for="additional_costs">
                    <ValidationProvider vid="reservations.lodging" rules="required" v-slot="{ errors }">
                      <Multiselect
                        id="additional_costs"
                        v-model="reserved_add_ons"
                        :options="additionalCostsName"
                        track-by="value"
                        label="text"
                        :multiple="true"
                        deselect-label="Unselect"
                      ></Multiselect>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-form>
              </ValidationObserver>

              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('addon-modal')">Close</b-button>
                <b-button form="addon-form" type="submit" variant="primary">Save</b-button>
              </template>
            </b-modal>

            <b-modal size="lg" id="delete-modal" title="Remove Add On" title-class="font-18">
              <p>
                Do you really want to delete this add on from this booking?
                <span class="text-danger">
                  (Warning: Booking's additional price will not be adjusted automatically)
                </span>
              </p>

              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('delete-modal')">Close</b-button>
                <b-button @click="deleteAddOn()" variant="danger">Delete</b-button>
              </template>
            </b-modal>
          </div>
        </div>

        <div class="card mb-4">
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-8">
                <h4 class="card-title">Discount</h4>
              </div>

              <div class="col-4 text-right">
                <b-button variant="outline-light"> <i class="fa fa-pencil-alt"></i> Edit</b-button>
              </div>
            </div>

            <div v-if="booking.attributes.reservations.data[0].attributes.discount">
              {{ booking.attributes.reservations.data[0].attributes.discount.title }}: ${{
                booking.attributes.reservations.data[0].attributes.discount_value
              }}
            </div>

            <div v-else>
              No discount selected for this booking
            </div>
          </div>
        </div>
      </div>
    </div>
  </Container>
</template>

<script>
import Container from "../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { showBooking, getLodgings, updateBooking, getAdditionalCosts } from "@/store/api";
import moment from "moment";

export default {
  page: {
    title: "Bookings",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Container,
    PageHeader,
  },

  data() {
    return {
      addOn: null,
      lodgings: [],
      additionalCosts: [],
      reserved_add_ons: [],
      booking: null,
      lodging: "",
      checkin: "",
      checkout: "",
      booking_obj: {
        customer_attributes: "",
        notes: "",
        reservations_attributes: [],
      },
      reservation: {
        id: "",
        checkin: "",
        checkout: "",
        lodging_id: "",
        adults: "",
        children: "",
        price: "",
        additional_price: "",
        discount_value: "",
        reserved_add_ons_attributes: "",
      },
      customer: {
        id: "",
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
      },
      items: [
        {
          text: "Home",
          to: "/",
        },
        {
          text: "Bookings",
          to: "/bookings",
        },
        {
          text: `Booking #${this.$route.params.id}`,
          active: true,
        },
      ],
    };
  },

  created() {
    getLodgings({ active: true }).then((response) => (this.lodgings = response));
    showBooking(this.$route.params.id).then((response) => {
      this.booking = response;
      getAdditionalCosts({ lodging: response.attributes.reservations.data[0].attributes.lodging.id }).then(
        (response) => {
          this.additionalCosts = response;
        }
      );
    });
  },

  computed: {
    totalCost() {
      return (
        parseFloat(this.booking.attributes.reservations.data[0].attributes.price) +
        parseFloat(this.booking.attributes.reservations.data[0].attributes.additional_price) -
        parseFloat(this.booking.attributes.reservations.data[0].attributes.discount_value)
      ).toFixed(2);
    },

    lodgingsName() {
      return this.lodgings.map((lodging) => {
        return { value: lodging.id, text: lodging.attributes.name, base_price: lodging.attributes.base_price };
      });
    },

    additionalCostsName() {
      return this.additionalCosts
        .filter((additionalCost) => {
          return !this.booking.attributes.reservations.data[0].attributes.reserved_add_ons.some((reserved_add_on) => {
            return reserved_add_on.additional_cost_id == additionalCost.id;
          });
        })
        .map((additionalCost) => {
          return {
            value: additionalCost.id,
            text: additionalCost.attributes.title,
            price: additionalCost.attributes.price,
            fixed_price: additionalCost.attributes.fixed_price,
          };
        });
    },
  },

  filters: {
    naturalDay(date) {
      return moment(date).format("DD/MM/YYYY");
    },

    naturalDayTime(date) {
      return moment(date).format("DD MMM, YYYY | hh:mm A");
    },
  },

  methods: {
    clearBooking() {
      this.booking_obj.customer_attributes = null;
      this.booking_obj.notes = "";
      this.booking_obj.reservations_attributes = [];
      this.reserved_add_ons = [];
    },

    disabledBeforeCheckin(date) {
      return date <= this.checkin;
    },

    disabledAfterCheckout(date) {
      if (!this.checkout) return false;
      return date >= this.checkout;
    },

    editReservation() {
      this.clearBooking();
      this.lodging = {
        value: this.booking.attributes.reservations.data[0].attributes.lodging.id,
        text: this.booking.attributes.reservations.data[0].attributes.lodging.name,
      };
      this.reservation.id = this.booking.attributes.reservations.data[0].id;
      this.checkin = moment(this.booking.attributes.reservations.data[0].attributes.checkin)._d;
      this.checkout = moment(this.booking.attributes.reservations.data[0].attributes.checkout)._d;
      this.booking_obj.notes = this.booking.attributes.notes;
      this.reservation.adults = this.booking.attributes.reservations.data[0].attributes.adults;
      this.reservation.children = this.booking.attributes.reservations.data[0].attributes.children;
      this.reservation.price = this.booking.attributes.reservations.data[0].attributes.price;
      this.reservation.additional_price = this.booking.attributes.reservations.data[0].attributes.additional_price;
      this.reservation.discount_value = this.booking.attributes.reservations.data[0].attributes.discount_value;
    },

    editCustomer() {
      this.clearBooking();
      this.customer.id = this.booking.attributes.customer.id;
      this.customer.first_name = this.booking.attributes.customer.first_name;
      this.customer.last_name = this.booking.attributes.customer.last_name;
      this.customer.email = this.booking.attributes.customer.email;
      this.customer.phone_number = this.booking.attributes.customer.phone_number;
    },

    editAddOn(addOn) {
      this.clearBooking();
      this.addOn = addOn;
    },

    deleteAddOn() {
      let reservations_attributes = [
        {
          id: this.booking.attributes.reservations.data[0].id,
          reserved_add_ons_attributes: [
            {
              id: this.addOn.id,
              additional_cost_id: this.addOn.additional_cost_id,
              _destroy: true,
            },
          ],
        },
      ];
      updateBooking({ reservations_attributes: reservations_attributes }, this.booking.id).then((response) => {
        this.$bvModal.hide("delete-modal");
        this.booking = response;
      });
    },

    onCustomerSubmit() {
      this.booking_obj.customer_attributes = this.customer;
      updateBooking(this.booking_obj, this.booking.id).then((response) => {
        this.$bvModal.hide("customer-modal");
        this.booking = response;
      });
    },

    onReservationSubmit() {
      this.reservation.lodging_id = this.lodging.value;
      this.reservation.checkin = moment(this.checkin).format("YYYY-MM-DD");
      this.reservation.checkout = moment(this.checkout).format("YYYY-MM-DD");
      this.booking_obj.reservations_attributes.push(this.reservation);
      updateBooking(this.booking_obj, this.booking.id)
        .then((response) => {
          this.$bvModal.hide("reservation-modal");
          this.$bvModal.hide("payment-modal");
          this.booking = response;
        })
        .catch((error) => {
          this.$refs.reservation_form.setErrors(error.response.data.errors);
        });
    },

    onAddOnSubmit() {
      let reservations_attributes = [
        {
          id: this.booking.attributes.reservations.data[0].id,
          reserved_add_ons_attributes: this.reserved_add_ons.map((reserved_add_on) => {
            return {
              title: reserved_add_on.text,
              additional_cost_id: reserved_add_on.value,
              price: reserved_add_on.price,
            };
          }),
        },
      ];
      updateBooking({ reservations_attributes: reservations_attributes }, this.booking.id).then((response) => {
        this.$bvModal.hide("addon-modal");
        this.booking = response;
      });
    },
  },
};
</script>

<style scoped>
#delete_addon {
  outline: none;
}
</style>
